/* Lightweight marketing context helper for attribution.
 * Stores context in both localStorage and a first-party cookie (when available) and
 * exposes a safe subset for checkout metadata.
 */

// trackGaLead removed 2026-02-22: GA4 Lead is now handled by GTM tag 34 (GA4 - Lead)
// which fires on the mm_lead dataLayer event. Direct gtag call would double-count.

export interface MarketingTouch {
  ts?: string;
  msclkid?: string | null;
  fbclid?: string | null;
  fbc?: string | null;
  fbp?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  channel?: string | null;
  entry_mode?: string | null;
}

export interface MarketingContext {
  session_id: string;
  first_touch: MarketingTouch;
  last_touch: MarketingTouch;
  bclid?: string | null;
  fbclid?: string | null;
  fbc?: string | null;
  fbp?: string | null;
  lp_variant?: string | null;
  // Deterministic email attribution (from autoresponder links)
  email_queue_id?: string | null;
  flags?: {
    from_email?: boolean;
    from_luna?: boolean;
  };
}

export interface CheckoutAttribution {
  sessionId?: string;
  msclkid?: string;
  fbclid?: string;
  fbc?: string;
  fbp?: string;
  bclid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  channel?: string;
  entry_mode?: string;
  lp_variant?: string;
  // Deterministic email attribution
  emailQueueId?: string;
  conversionSource?: string;
  eventId?: string;
  // Luna Chat attribution fields
  chatSessionId?: string;    // UUID FK to luna_chat_sessions.id
  assistantId?: string;      // Azure AI Agent ID (e.g., "asst_MFOu5cyETlfyw9o8wwkn7FKR")
  modelVariant?: string;     // Luna model variant (e.g., "luna_v1")
  chatTurns?: number;        // Number of conversation turns
}

const STORAGE_KEY = 'mm_marketing_ctx';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 180; // 180 days

const isBrowser = typeof window !== 'undefined';

function safeNowIso(): string {
  return new Date().toISOString();
}

function readCookieValue(name: string): string | null {
  if (!isBrowser || typeof document === 'undefined') return null;
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  const prefix = `${name}=`;
  for (const entry of cookies) {
    if (!entry.startsWith(prefix)) continue;
    return decodeURIComponent(entry.slice(prefix.length));
  }
  return null;
}

function uuid(): string {
  if (isBrowser && typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback RFC4122-ish random
  const template = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
  return template.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
}

function readFromStorage(): MarketingContext | null {
  if (!isBrowser) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as MarketingContext;
  } catch (err) {
    console.warn('[marketingContext] Failed to read context:', err);
    return null;
  }
}

function persistContext(ctx: MarketingContext): void {
  if (!isBrowser) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ctx));
  } catch (err) {
    console.warn('[marketingContext] Failed to persist context to localStorage:', err);
  }

  // Best-effort cookie (SameSite=Lax, domain=.moonmanifest.com when applicable)
  try {
    const parts = [
      `${STORAGE_KEY}=${encodeURIComponent(JSON.stringify(ctx))}`,
      'path=/',
      `max-age=${COOKIE_MAX_AGE}`,
      'SameSite=Lax'
    ];
    const isHttps = window.location.protocol === 'https:';
    if (isHttps) {
      parts.push('Secure');
    }
    // Only set the parent domain when we are already on it
    if (window.location.hostname.endsWith('moonmanifest.com')) {
      parts.push('domain=.moonmanifest.com');
    }
    document.cookie = parts.join('; ');
  } catch (err) {
    console.warn('[marketingContext] Failed to persist context to cookie:', err);
  }
}

function classifyChannel(params: URLSearchParams, referrer?: string): string {
  const utmMedium = params.get('utm_medium') || params.get('mm_channel');
  if (params.get('fbclid')) return 'paid_facebook';
  if (params.get('msclkid')) return 'paid_microsoft';
  if ((params.get('gclid'))) return 'paid_google';
  if (utmMedium === 'email') return 'email';
  if (utmMedium === 'social') return 'social';
  if (referrer && /google|bing|yahoo|duckduckgo|baidu|yandex/i.test(referrer)) return 'seo';
  if (!referrer) return 'direct';
  return 'referral';
}

function buildTouch(params: URLSearchParams, referrer: string, entryMode: string): MarketingTouch {
  const fbc = params.get('fbc') || readCookieValue('_fbc');
  const fbp = params.get('fbp') || readCookieValue('_fbp');
  const getParam = (snake: string, camel?: string): string | null => {
    return params.get(snake) || (camel ? params.get(camel) : null);
  };

  return {
    ts: safeNowIso(),
    msclkid: getParam('msclkid'),
    fbclid: getParam('fbclid'),
    fbc,
    fbp,
    // Support both snake_case and common camelCase variants used in some ad systems
    utm_source: getParam('utm_source', 'utmSource'),
    utm_medium: getParam('utm_medium', 'utmMedium'),
    utm_campaign: getParam('utm_campaign', 'utmCampaign'),
    utm_content: getParam('utm_content', 'utmContent'),
    utm_term: getParam('utm_term', 'utmTerm'),
    channel: classifyChannel(params, referrer),
    entry_mode: entryMode
  };
}

function mergeTouch(existing: MarketingTouch, next: MarketingTouch): MarketingTouch {
  return {
    ts: next.ts || existing.ts,
    msclkid: next.msclkid || existing.msclkid,
    fbclid: next.fbclid || existing.fbclid,
    fbc: next.fbc || existing.fbc,
    fbp: next.fbp || existing.fbp,
    utm_source: next.utm_source || existing.utm_source,
    utm_medium: next.utm_medium || existing.utm_medium,
    utm_campaign: next.utm_campaign || existing.utm_campaign,
    utm_content: next.utm_content || existing.utm_content,
    utm_term: next.utm_term || existing.utm_term,
    channel: next.channel || existing.channel,
    entry_mode: next.entry_mode || existing.entry_mode
  };
}

function ensureContextInitialized(): MarketingContext | null {
  if (!isBrowser) return null;
  const currentUrl = window.location.href;
  const referrer = document.referrer || '';
  const params = new URL(currentUrl).searchParams;
  const entryMode = params.get('mm_entry') || (window.location.pathname.startsWith('/lp') ? 'landing' : 'cta');
  const lpVariant = params.get('mm_lp') || params.get('lp_variant') || params.get('vsl_variant');
  const existing = readFromStorage();

  const nextTouch = buildTouch(params, referrer, entryMode);
  const ctx: MarketingContext = existing || {
    session_id: uuid(),
    first_touch: nextTouch,
    last_touch: nextTouch,
    bclid: nextTouch.msclkid || null,
    fbclid: nextTouch.fbclid || null,
    fbc: nextTouch.fbc || null,
    fbp: nextTouch.fbp || null,
    lp_variant: lpVariant || null,
    email_queue_id: params.get('eqid') || null,
    flags: {}
  };

  // Update last touch each time; keep first touch stable
  ctx.last_touch = mergeTouch(ctx.last_touch, nextTouch);
  ctx.fbc = ctx.last_touch.fbc || ctx.first_touch.fbc || ctx.fbc || null;
  ctx.fbp = ctx.last_touch.fbp || ctx.first_touch.fbp || ctx.fbp || null;
  if (!ctx.lp_variant && lpVariant) {
    ctx.lp_variant = lpVariant;
  }

  const eqid = params.get('eqid');
  if (!ctx.email_queue_id && eqid) {
    ctx.email_queue_id = eqid;
  }

  // Set flags
  ctx.flags = ctx.flags || {};
  if (ctx.email_queue_id || (ctx.last_touch.utm_medium || '').toLowerCase() === 'email') {
    ctx.flags.from_email = true;
  }

  persistContext(ctx);
  return ctx;
}

export function getMarketingContext(): MarketingContext | null {
  if (!isBrowser) return null;
  const ctx = readFromStorage();
  if (ctx && ctx.session_id) {
    return ctx;
  }
  return ensureContextInitialized();
}

export function buildCheckoutAttribution(
  eventType: 'checkout_start' | 'purchase' | 'lead' = 'checkout_start',
  overrides: Partial<CheckoutAttribution> = {}
): CheckoutAttribution | undefined {
  const ctx = ensureContextInitialized();
  if (!ctx) return undefined;

  const msclkid = ctx.last_touch.msclkid || ctx.first_touch.msclkid || undefined;
  const fbclid = ctx.last_touch.fbclid || ctx.first_touch.fbclid || undefined;
  const fbc = ctx.last_touch.fbc || ctx.first_touch.fbc || ctx.fbc || undefined;
  const fbp = ctx.last_touch.fbp || ctx.first_touch.fbp || ctx.fbp || undefined;
  const utm = ctx.last_touch || ctx.first_touch;

  return {
    sessionId: ctx.session_id,
    msclkid: msclkid || undefined,
    fbclid: fbclid || undefined,
    fbc: fbc || undefined,
    fbp: fbp || undefined,
    bclid: ctx.bclid || undefined,
    utm_source: utm.utm_source || undefined,
    utm_medium: utm.utm_medium || undefined,
    utm_campaign: utm.utm_campaign || undefined,
    utm_content: utm.utm_content || undefined,
    utm_term: utm.utm_term || undefined,
    channel: utm.channel || undefined,
    entry_mode: utm.entry_mode || undefined,
    lp_variant: ctx.lp_variant || undefined,
    emailQueueId: ctx.email_queue_id || undefined,
    eventId: overrides.eventId || generateEventId(eventType, ctx.session_id),
    conversionSource: overrides.conversionSource || undefined,
    // Luna Chat attribution - pass through from overrides
    chatSessionId: overrides.chatSessionId || undefined,
    assistantId: overrides.assistantId || undefined,
    modelVariant: overrides.modelVariant || undefined,
    chatTurns: overrides.chatTurns || undefined,
    ...overrides
  };
}

export function generateEventId(eventType: string, sessionId?: string): string {
  const baseSession = sessionId || 'anon';
  const ts = Date.now();
  const rand = Math.random().toString(16).slice(2, 6);
  return `${eventType}_${baseSession}_${ts}_${rand}`;
}

export function pushMarketingEvent(
  eventName: 'mm_page_view' | 'mm_lead' | 'mm_checkout_start' | 'mm_purchase',
  payload: Partial<CheckoutAttribution & { profileId?: string; value?: number; currency?: string; conversion_source?: string }>
): void {
  if (!isBrowser) return;
  const ctx = ensureContextInitialized();
  if (!ctx) return;

  const base = buildCheckoutAttribution(
    eventName === 'mm_lead' ? 'lead' : eventName === 'mm_purchase' ? 'purchase' : 'checkout_start',
    {
      conversionSource: payload.conversionSource || payload.conversion_source,
      eventId: payload.eventId
    }
  );

  if (!base) return;

  // Persist experiment variants into the dataLayer so GTM can forward them to GA4.
  // We read them from sessionStorage because some experiments assign without putting
  // the variant in the visible URL.
  // Default all experiment variants to 'control' so GA4 never records (not set)
  // for these critical A/B dimensions.
  //
  // We still allow explicit URL/session overrides elsewhere (VSL page, heroVariants.ts).
  let vslVariant: string = 'control';
  let heroVariant: string = 'control';
  let hookVariant: string = 'control';
  try {
    vslVariant = sessionStorage.getItem('mm_vsl_cta_variant') || 'control';
    hookVariant = sessionStorage.getItem('mm_vsl_hook_variant') || 'control';

    // Ensure hero variant is assigned on first touch so GA4 never sees (not set).
    // Respect explicit URL override (?hero_variant=A) if present.
    const HERO_KEYS = ['control', 'A', 'B', 'C'];
    const params = new URLSearchParams(window.location.search);
    const explicit = params.get('hero_variant');
    if (explicit && HERO_KEYS.includes(explicit)) {
      heroVariant = explicit;
      sessionStorage.setItem('mm_hero_variant', explicit);
    } else {
      const stored = sessionStorage.getItem('mm_hero_variant');
      if (stored && HERO_KEYS.includes(stored)) {
        heroVariant = stored;
      } else {
        const picked = HERO_KEYS[Math.floor(Math.random() * HERO_KEYS.length)];
        heroVariant = picked;
        sessionStorage.setItem('mm_hero_variant', picked);
      }
    }
  } catch (_) {
    // ignore
  }

  const dataLayerPayload: Record<string, unknown> = {
    event: eventName,
    session_id: base.sessionId,
    msclkid: base.msclkid,
    fbclid: base.fbclid,
    fbc: base.fbc,
    fbp: base.fbp,
    bclid: base.bclid,
    utm_source: base.utm_source,
    utm_medium: base.utm_medium,
    utm_campaign: base.utm_campaign,
    utm_content: base.utm_content,
    utm_term: base.utm_term,
    channel: base.channel,
    entry_mode: base.entry_mode,
    lp_variant: base.lp_variant,
    email_queue_id: base.emailQueueId,
    event_id: base.eventId,
    conversion_source: base.conversionSource,
    vsl_variant: vslVariant,
    hero_variant: heroVariant,
    hook_variant: hookVariant,
    profile_id: payload.profileId,
    value: payload.value,
    currency: payload.currency || 'USD'
  };

  try {
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push(dataLayerPayload);
  } catch (err) {
    console.warn('[marketingContext] Failed to push dataLayer event', err);
  }

  // Ensure we have a GA4 event for mm_page_view even if GTM does not yet forward
  // this custom dataLayer event. This unblocks hero_variant reporting.
  // We intentionally do NOT do this for mm_lead to avoid double-counting (GTM tag 34).
  if (eventName === 'mm_page_view') {
    try {
      const gtag = (window as any).gtag;
      if (typeof gtag === 'function') {
        const { event, ...params } = dataLayerPayload as any;
        gtag('event', 'mm_page_view', params);
      }
    } catch (_) {
      // ignore
    }
  }

  // GA4 generate_lead is now fired exclusively via GTM tag 34 (GA4 - Lead)
  // triggered by the mm_lead dataLayer event above. No direct gtag call needed.
}
